import { useCallback, useEffect, useMemo, useState } from 'react';
import { BorderedInput, Button, Typography } from '@navi/web-ui/lib/primitives';
import Dropdown from 'src/components/Dropdown';
import styles from './SendPaymentLinkCard.module.scss';
import { SelectPickerOptionProps } from 'src/components/interfaces';
import { useForm } from 'react-hook-form';
import ShareIcon from 'src/assets/icons/ShareIcon';
import cx from 'classnames';
import { RootState } from 'src/store';
import { useSelector } from 'react-redux';
import { sendDeepLink, sendPaymentLink } from '../CaseDetails/actions/paymentsActions';
import { PaymentPayload, PaymentType } from '../CaseDetails/interfaces/Payments.type';
import { HumanReminderType } from '../../reducers/commonSlice';
import { addClickstreamEvent } from '../../service/clickStreamEventService';
import { HRC_CLICKSTREAM } from '../../service/clickStream.constant';
import AlertOutlineIcon from '@navi/web-ui/lib/icons/AlertOutlineIcon/AlertOutlineIcon';
import { SEND_PAYMENT_ERROR_COLOR } from './constants/dashboard.constants';
import { ExtensionHandler } from 'src/utils/extension.utils';
import {
  DEEPLINK_TYPE,
  DeepLinkRequest,
  sendDeeplinkOptions
} from '@cp/src/constants/Common.constants';
import { OptionPosition } from '@cp/src/components/Dropdown/interfaces';
import { maskString } from '@cp/utils/commonUtils';

interface SendPaymentLinkCardProps {
  primaryNumber: string;
  connectedNumber: string;
}

interface IPaymentsFormValues {
  alternateContactNumber: string;
  amount: string;
}

const SendPaymentLinkCard = (props: SendPaymentLinkCardProps) => {
  const { primaryNumber, connectedNumber } = props;
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    watch,
    setError,
    formState: { errors }
  } = useForm<IPaymentsFormValues>({
    mode: 'onChange'
  });
  const [sendLink, setSendLink] = useState<boolean>(false);
  const [amountToPay, setAmountToPay] = useState<number>(0);
  const [paymentLinkOptionsMap, setPaymentLinkOptionsMap] = useState({});
  const customerDetails = useSelector(
    (state: RootState) =>
      state.common.humanReminderCustomerDetails?.humanReminderResponse?.customerDetails
  );
  const loanDetails = useSelector(
    (state: RootState) =>
      state.common.humanReminderCustomerDetails?.humanReminderResponse?.loanDetails
  );
  const initialPaymentLinkOptions = useSelector(
    (state: RootState) =>
      state.common.humanReminderCustomerDetails?.humanReminderResponse?.paymentLinkOptions
  );
  const reminderType = useSelector(
    (state: RootState) =>
      state.common.humanReminderCustomerDetails?.humanReminderResponse?.reminderType
  );
  const campaignId = useSelector((state: RootState) => state.humanReminder?.campaignId);
  const isCallConnected = useSelector((state: RootState) => state.humanReminder.isCallConnected);
  const reminderRefId = useSelector((state: RootState) => state.humanReminder.reminderRefId);

  const [paymentLinkOptions, setPaymentLinkOptions] = useState<any>();
  const validatePhoneNumber = value => {
    if (!value) {
      clearErrors();
      return true;
    }
    if (Number.isNaN(Number(value)) || !value || value.length !== 10) {
      return 'Enter a valid phone number';
    }
  };

  useEffect(() => {
    setPaymentLinkOptions(
      initialPaymentLinkOptions ? [...initialPaymentLinkOptions, ...sendDeeplinkOptions] : null
    );
  }, [initialPaymentLinkOptions]);

  const getDefaultValue = useCallback(() => {
    if (reminderType === HumanReminderType.PRE_DUE) {
      return paymentLinkOptions.filter(option => option?.value === PaymentType.EARLY_PAYMENT)?.[0]
        ?.amount?.amount;
    } else if (reminderType === HumanReminderType.POST_DUE) {
      return paymentLinkOptions?.filter(option => option?.value === PaymentType.EMI)?.[0]?.amount
        ?.amount;
    }
    return 0;
  }, [paymentLinkOptions]);
  const outstandingAmount = loanDetails?.totalAmountDue?.amount.toFixed();
  const loanId = loanDetails?.loanAccountNumber;
  const customerId = customerDetails?.customerReferenceId;
  const [selectedLinkType, setSelectedLinkType] = useState<SelectPickerOptionProps>();

  useEffect(() => {
    if (selectedLinkType?.value) {
      setAmountToPay(getDefaultValue() || 0);
      setValue('amount', String(getDefaultValue() || 0));
    }
  }, [selectedLinkType, paymentLinkOptions]);

  const paymentDropdownHandler = useCallback(
    (selectedOption: SelectPickerOptionProps) => {
      paymentLinkOptions?.filter((option: any) => {
        if (option?.value === selectedOption?.value) {
          setSelectedLinkType(selectedOption);
          const amount = option?.amount?.amount;
          if (selectedOption?.value !== PaymentType.CUSTOM) {
            setAmountToPay(amount);
            setValue('amount', amount);
          }
          addClickstreamEvent(HRC_CLICKSTREAM.HRC_PAYMENT_SEND_LINK_AMOUNT_TYPE_CHANGED, {
            lan: loanId,
            customerReferenceId: customerDetails?.customerReferenceId,
            amountType: selectedOption.value,
            primaryNumber,
            connectedNumber,
            alternateContactNumber: getValues('alternateContactNumber'),
            amount: amount,
            campaignId: campaignId,
            disconnected: !isCallConnected,
            reminderRefId,
            ameyoStatus: ExtensionHandler.currentAmeyoStatus()
          });
        }
      });
      const optionsMap: Record<string, any> = {};
      paymentLinkOptions.forEach(option => {
        optionsMap[option.value] = option;
      });
      setPaymentLinkOptionsMap({ ...optionsMap });
    },
    [paymentLinkOptions]
  );

  const disableAmountInput = useMemo(
    () => selectedLinkType?.value === PaymentType.EMI,
    [selectedLinkType]
  );

  useEffect(() => {
    if (reminderType === HumanReminderType.PRE_DUE) {
      setSelectedLinkType(
        paymentLinkOptions
          ?.map(option => ({
            label: option.label,
            value: option.value,
            isDisabled: false
          }))
          .find(option => option.value === PaymentType.EARLY_PAYMENT)
      );
    } else if (reminderType === HumanReminderType.POST_DUE) {
      setSelectedLinkType(
        paymentLinkOptions
          ?.map(option => ({
            label: option.label,
            value: option.value,
            isDisabled: false
          }))
          .find(option => option.value === PaymentType.EMI)
      );
    }
  }, [paymentLinkOptions]);

  useEffect(() => {
    if (connectedNumber) {
      setValue('alternateContactNumber', connectedNumber);
    }
  }, [connectedNumber]);

  const generatePayload = (
    formData: IPaymentsFormValues,
    isForeclosure?: boolean
  ): PaymentPayload => {
    const { amount } = formData;
    const payload: PaymentPayload = {
      type: selectedLinkType?.value as PaymentType,
      phoneNumber: formData.alternateContactNumber
        ? formData.alternateContactNumber
        : connectedNumber,
      customAmount: PaymentType.CUSTOM === selectedLinkType?.value ? amount : String(amountToPay),
      loanAccountNumber: loanId
    };
    if (isForeclosure) {
      delete payload.customAmount;
    }
    return payload;
  };

  const handleSendLink = (formData: IPaymentsFormValues) => {
    setSendLink(true);
    if (Object.keys(DEEPLINK_TYPE).includes(selectedLinkType?.value as string)) {
      handleSendDeepLink(formData, selectedLinkType);
      return;
    }
    const payload = generatePayload(formData);
    const clickStreamPayload = {
      lan: payload.loanAccountNumber,
      customerReferenceId: customerDetails?.customerReferenceId,
      amountType: payload?.type,
      primaryNumber,
      connectedNumber,
      alternateContactNumber: payload?.phoneNumber,
      amount: payload?.customAmount,
      campaignId: campaignId,
      disconnected: !isCallConnected
    };
    addClickstreamEvent(HRC_CLICKSTREAM.HRC_PAYMENT_SEND_LINK_CLICKED, {
      clickStreamPayload,
      reminderRefId,
      campaignId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
    const sendSuccessEvent = () => {
      addClickstreamEvent(HRC_CLICKSTREAM.HRC_PAYMENT_SEND_LINK_SUCCESSFULL, {
        clickStreamPayload,
        reminderRefId,
        campaignId,
        ameyoStatus: ExtensionHandler.currentAmeyoStatus()
      });
    };
    const sendFailureEvent = error => {
      addClickstreamEvent(HRC_CLICKSTREAM.HRC_PAYMENT_SEND_LINK_FAILURE, {
        ...clickStreamPayload,
        error,
        reminderRefId,
        campaignId,
        ameyoStatus: ExtensionHandler.currentAmeyoStatus()
      });
    };
    sendPaymentLink(loanId, customerId, payload, setSendLink, sendSuccessEvent, sendFailureEvent);
  };

  const handleSendDeepLink = (
    formData: IPaymentsFormValues,
    selectedLinkType?: SelectPickerOptionProps
  ) => {
    const payload: DeepLinkRequest = {
      type: selectedLinkType?.value as DEEPLINK_TYPE,
      phoneNumber: formData.alternateContactNumber
    };
    if (payload.type === DEEPLINK_TYPE.EMI_DATE_CHANGE) {
      payload.loanAccountNumber = loanId;
    }
    sendDeepLink(payload, setSendLink);
  };

  const getAmountInputType = () => {
    if (Object.keys(DEEPLINK_TYPE).includes(selectedLinkType?.value as string)) {
      return 'text';
    }
    return 'number';
  };

  const maskedConnectedNumber = useMemo(() => {
    return maskString(`+91 ${connectedNumber}`, 6, 4);
  }, [connectedNumber]);

  return (
    <div className={styles.sendPaymentContainer}>
      <form className={Object.keys(errors).length ? styles.paymentFormError : styles.paymentForm}>
        <div className={styles.sendLinkContainer}>
          <div>
            <Typography variant="p3" className={styles.text}>
              Connected Number
            </Typography>
            <Typography variant="p3" className={cx([styles.value, styles.primaryNumberValue])}>
              {maskedConnectedNumber}
            </Typography>
          </div>
          <div>
            <Typography variant="p3" className={styles.text}>
              Alternate Number
            </Typography>
            <Typography variant="p3" className={styles.value}>
              <BorderedInput
                id="phoneNumber"
                type="tel"
                maxLength={10}
                minLength={10}
                {...register(`alternateContactNumber`, {
                  validate: validatePhoneNumber
                })}
                error={errors?.['alternateContactNumber']?.message || ''}
                Icon={<AlertOutlineIcon color={SEND_PAYMENT_ERROR_COLOR} />}
                containerClassName={styles.amountInput}
                labelClassName={styles.text}
                helperTextClasses={styles.helperText}
                placeholder={'Type Number'}
                onChange={e => {
                  if (!e.target.value || e.target.value.length === 0) clearErrors();
                  if (Number.isNaN(Number(e.target.value))) {
                    setError('alternateContactNumber', {
                      type: 'manual',
                      message: 'Enter a valid phone number'
                    });
                  }
                  if (e.target.value?.length === 10) {
                    if (!Number.isNaN(Number(e.target.value))) {
                      clearErrors();
                    }
                    addClickstreamEvent(
                      HRC_CLICKSTREAM.HRC_PAYMENT_SEND_LINK_ALTERNATE_NUMBER_CHANGED,
                      {
                        lan: loanId,
                        customerReferenceId: customerDetails?.customerReferenceId,
                        amountType: selectedLinkType,
                        primaryNumber,
                        connectedNumber,
                        alternateContactNumber: e.target.value,
                        amount: getValues('amount'),
                        campaignId: campaignId,
                        disconnected: !isCallConnected,
                        reminderRefId,
                        ameyoStatus: ExtensionHandler.currentAmeyoStatus()
                      }
                    );
                  }
                }}
              />
            </Typography>
          </div>

          <div key={selectedLinkType?.value}>
            <Typography variant="p3" className={styles.text}>
              Link Type
            </Typography>
            <Dropdown
              defaultSelected={selectedLinkType}
              options={
                paymentLinkOptions?.map(option => ({
                  label: option.label,
                  value: option.value,
                  isDisabled: false
                })) || []
              }
              onSelectionChange={paymentDropdownHandler}
              placeholder="Select payment type"
              containerClasses={cx(styles.value, styles.dropDown)}
              optionPosition={OptionPosition.top}
            />
          </div>
          <div key={`${amountToPay}_${selectedLinkType?.value}`}>
            {selectedLinkType?.value === PaymentType.CUSTOM ? (
              <BorderedInput
                inputLabel={'Amount'}
                value={amountToPay}
                containerClassName={cx(styles.amountInput, styles.amountInputValue)}
                labelClassName={styles.text}
                LeftInputAdornment="₹"
                type={'number'}
                error={errors?.['amount']?.message || ''}
                Icon={<AlertOutlineIcon color={SEND_PAYMENT_ERROR_COLOR} />}
                {...register('amount', {
                  required: true,
                  min: { value: 1, message: 'Please enter a greater value' },
                  max: {
                    value: outstandingAmount,
                    message: `Amount exceeds overdue`
                  }
                })}
                onChange={e => {
                  addClickstreamEvent(HRC_CLICKSTREAM.HRC_PAYMENT_SEND_LINK_AMOUNT_CHANGED, {
                    lan: loanId,
                    customerReferenceId: customerDetails?.customerReferenceId,
                    amountType: selectedLinkType,
                    primaryNumber,
                    connectedNumber,
                    alternateContactNumber: getValues('alternateContactNumber'),
                    amount: e.target.value,
                    campaignId: campaignId,
                    disconnected: !isCallConnected,
                    reminderRefId,
                    ameyoStatus: ExtensionHandler.currentAmeyoStatus()
                  });
                }}
              />
            ) : (
              <BorderedInput
                inputLabel={'Amount'}
                value={
                  paymentLinkOptions?.filter(e => {
                    return e.value === selectedLinkType?.value;
                  })?.[0]?.amount?.amount || 0
                }
                containerClassName={cx(styles.amountInput, styles.amountInputValue)}
                labelClassName={styles.text}
                LeftInputAdornment={getAmountInputType() === 'number' ? '₹' : null}
                type={getAmountInputType()}
                disabled
              />
            )}
          </div>
          <div className={styles.shareButton}>
            <Button
              onClick={handleSubmit(handleSendLink)}
              name={'link_generate'}
              type={'submit'}
              disabled={sendLink || errors?.alternateContactNumber}
              className={cx(
                styles.shareLink,
                (errors.amount || errors.alternateContactNumber) && styles.mb20
              )}
            >
              <ShareIcon />
              <span className={styles.shareText}> {sendLink ? 'Sending link' : 'Send link'}</span>
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SendPaymentLinkCard;
